/**
 * Checkout Channel Invite Router
 * 
 * Página intermedia inteligente que detecta el país del usuario y lo redirige
 * al checkout correcto:
 * - Argentina → /checkout-ar-channel-invite (MercadoPago Bricks)
 * - Internacional → /checkout-channel-invite (Stripe)
 * 
 * UX optimizada:
 * - Detección rápida (< 800ms)
 * - Loading elegante con animaciones
 * - Fallback instantáneo si la detección falla
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CountryDetectionLoader } from "@/components/CountryDetectionLoader";
import { useCountryDetectionWithApi } from "@/lib/useCountryDetectionWithApi";

export default function CheckoutChannelInviteRouter() {
  const [searchParams] = useSearchParams();
  const [hasRedirected, setHasRedirected] = useState(false);
  
  // Detectar país con API fallback
  const { country, isLoading, confidence, city } = useCountryDetectionWithApi(
    true, // Habilitar API
    800   // Timeout de 800ms
  );

  // Preservar parámetros de URL
  const pack = searchParams.get("pack") || "channel_invite";
  const lang = searchParams.get("lang") || "es";
  const ref = searchParams.get("ref") || "";
  const code = searchParams.get("code") || "";

  useEffect(() => {
    // No redirigir si ya se hizo
    if (hasRedirected) return;
    
    // Estrategia de redirección inteligente:
    // 1. Si tenemos alta confianza (Worker o API) → redirigir inmediatamente
    // 2. Si tenemos confianza media y terminó loading → redirigir
    // 3. Si no hay país después de loading → usar US por defecto
    const shouldRedirect = 
      confidence === "high" || 
      (!isLoading && (confidence === "medium" || confidence === "low"));

    if (!shouldRedirect) return;

    // Construir URL de destino con parámetros
    const buildCheckoutUrl = (basePath: string) => {
      const params = new URLSearchParams();
      params.set("pack", pack);
      params.set("lang", lang);
      if (ref) params.set("ref", ref);
      if (code) params.set("code", code);
      if (country) params.set("country", country);
      return `${basePath}?${params.toString()}`;
    };

    // Decidir checkout según país
    let checkoutUrl: string;
    const detectedCountry = country || "US"; // Fallback a US si no se detectó
    
    if (detectedCountry === "AR") {
      console.log("[CheckoutChannelInviteRouter] 🇦🇷 Argentina detected - routing to MercadoPago Bricks", {
        confidence,
        country: detectedCountry,
      });
      checkoutUrl = buildCheckoutUrl("/checkout-ar-channel-invite");
    } else {
      console.log("[CheckoutChannelInviteRouter] 🌎 International detected - routing to Stripe", {
        confidence,
        country: detectedCountry,
      });
      checkoutUrl = buildCheckoutUrl("/checkout-channel-invite");
    }

    // Pequeño delay para que el usuario vea el país detectado (mejor UX)
    const redirectDelay = confidence === "high" ? 300 : 500;
    
    setTimeout(() => {
      setHasRedirected(true);
      window.location.href = checkoutUrl;
    }, redirectDelay);
  }, [country, isLoading, confidence, hasRedirected, pack, lang, ref, code]);

  // Mostrar loading mientras detecta
  return (
    <CountryDetectionLoader
      message="Preparando tu checkout"
      detectedCountry={country}
      confidence={confidence}
      city={city}
    />
  );
}
